import { useEffect, useState } from "react";
import type {
    ConsentAdminTabId,
    ConsentCreationDrawerState,
    ConsentDeleteDialogState,
    ConsentGovernanceItemType,
    ConsentItemModalState,
    ConsentItemVersionModalState,
    ConsentPreviewDialogState,
} from "../consentGovernanceTypes";
import type {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { DOCUMENTS_TAB, OPT_INS_TAB } from "../consentGovernanceTypes";

type ConsentCreationCreatedPayload = {
    item: ConsentGovernanceItem;
    visibleLocale: string;
};

type UseConsentGovernanceDialogsInput = {
    consentItems: ConsentGovernanceItem[];
    setSelectedLocale: (locale: string) => void;
    switchToTab: (tabId: ConsentAdminTabId) => void;
};

export const useConsentGovernanceDialogs = ({
    consentItems,
    setSelectedLocale,
    switchToTab,
}: UseConsentGovernanceDialogsInput) => {
    const [itemModalState, setItemModalState] = useState<ConsentItemModalState>({
        open: false,
        itemType: "document",
    });
    const [versionModalState, setVersionModalState] = useState<ConsentItemVersionModalState>({
        open: false,
    });
    const [creationDrawerState, setCreationDrawerState] = useState<ConsentCreationDrawerState>({
        open: false,
        initialType: "document",
    });
    const [deleteDialogState, setDeleteDialogState] = useState<ConsentDeleteDialogState>({
        open: false,
    });
    const [previewDialogState, setPreviewDialogState] = useState<ConsentPreviewDialogState>({
        open: false,
    });
    const [highlightedCreatedItemId, setHighlightedCreatedItemId] = useState<number | null>(null);

    const isConsentEditorOpen = itemModalState.open || versionModalState.open || creationDrawerState.open;

    useEffect(() => {
        if (!isConsentEditorOpen) {
            return;
        }

        const previousValue = document.body.dataset.consentEditorOpen;
        document.body.dataset.consentEditorOpen = "true";

        return () => {
            if (previousValue === undefined) {
                delete document.body.dataset.consentEditorOpen;
                return;
            }

            document.body.dataset.consentEditorOpen = previousValue;
        };
    }, [isConsentEditorOpen]);

    useEffect(() => {
        if (highlightedCreatedItemId === null) {
            return;
        }

        const timeout = window.setTimeout(() => setHighlightedCreatedItemId(null), 5000);

        return () => window.clearTimeout(timeout);
    }, [highlightedCreatedItemId]);

    const openCreateConsentDrawer = (itemType: ConsentGovernanceItemType) => {
        setCreationDrawerState({
            open: true,
            initialType: itemType,
        });
    };

    const closeCreationDrawer = () => {
        setCreationDrawerState((prev) => ({ ...prev, open: false }));
    };

    const openEditItemModal = (consentItem: ConsentGovernanceItem) => {
        setItemModalState({ open: true, itemType: consentItem.type, consentItem });
    };

    const closeItemModal = () => {
        setItemModalState((prev) => ({ ...prev, open: false, consentItem: undefined }));
    };

    const openCreateVersionModal = (
        consentItemType?: ConsentGovernanceItemType,
        preselectedConsentItem?: ConsentGovernanceItem,
    ) => {
        setVersionModalState({ open: true, consentItemType, preselectedConsentItem });
    };

    const openEditVersionModal = (consentVersion: ConsentGovernanceVersion) => {
        setVersionModalState({ open: true, consentVersion });
    };

    const closeVersionModal = () => {
        setVersionModalState((prev) => ({
            ...prev,
            open: false,
            consentVersion: undefined,
            consentItemType: undefined,
            preselectedConsentItem: undefined,
        }));
    };

    const openDeleteItemDialog = (item: ConsentGovernanceItem) => {
        setDeleteDialogState({
            open: true,
            target: { kind: "item", item },
        });
    };

    const openDeleteVersionDialog = (version: ConsentGovernanceVersion) => {
        setDeleteDialogState({
            open: true,
            target: { kind: "version", version },
        });
    };

    const closeDeleteDialog = () => {
        setDeleteDialogState({ open: false });
    };

    const openBulkDeleteItemsDialog = (items: ConsentGovernanceItem[]) => {
        setDeleteDialogState({
            open: true,
            target: { kind: "items", items },
        });
    };

    const openBulkDeleteVersionsDialog = (versions: ConsentGovernanceVersion[]) => {
        setDeleteDialogState({
            open: true,
            target: { kind: "versions", versions },
        });
    };

    const openPreviewDialog = (item: ConsentGovernanceItem, version?: ConsentGovernanceVersion) => {
        if (!version) {
            return;
        }

        setPreviewDialogState({
            open: true,
            item,
            version,
        });
    };

    const openVersionPreviewDialog = (version: ConsentGovernanceVersion) => {
        const itemId =
            typeof version.consentItem === "object" && version.consentItem !== null ? version.consentItem.id : null;
        const item = itemId ? consentItems.find((candidate) => candidate.id === itemId) : undefined;

        setPreviewDialogState({
            open: true,
            item,
            version,
        });
    };

    const closePreviewDialog = () => {
        setPreviewDialogState({ open: false });
    };

    const handleItemModalSuccess = (savedItem: ConsentGovernanceItem) => {
        if (itemModalState.consentItem) {
            return;
        }

        openCreateVersionModal(savedItem.type, savedItem);
    };

    const handleConsentCreationDrawerCreated = ({ item, visibleLocale }: ConsentCreationCreatedPayload) => {
        setSelectedLocale(visibleLocale);
        setHighlightedCreatedItemId(item.id);
        switchToTab(item.type === "document" ? DOCUMENTS_TAB : OPT_INS_TAB);
    };

    return {
        closeCreationDrawer,
        closeDeleteDialog,
        closeItemModal,
        closePreviewDialog,
        closeVersionModal,
        creationDrawerState,
        deleteDialogState,
        handleConsentCreationDrawerCreated,
        handleItemModalSuccess,
        highlightedCreatedItemId,
        itemModalState,
        openBulkDeleteItemsDialog,
        openBulkDeleteVersionsDialog,
        openCreateConsentDrawer,
        openCreateVersionModal,
        openDeleteItemDialog,
        openDeleteVersionDialog,
        openEditItemModal,
        openEditVersionModal,
        openPreviewDialog,
        openVersionPreviewDialog,
        previewDialogState,
        versionModalState,
    };
};
